import { EmailFieldUI } from '@axo/form/feature/form-input/EmailField/EmailFieldUI';
import { NumberFieldUI } from '@axo/form/feature/form-input/NumberField/NumberFieldUI';
import { PhoneFieldUI } from '@axo/form/feature/form-input/PhoneField/PhoneFieldUI';
import { useTranslation } from '@axo/shared/i18n';
import { useMemo } from 'react';
import { RegisterOptions, useFormContext } from 'react-hook-form';
import { useAxoFormContext } from '../../../../../context/AxoForm/AxoForm';
import { useEmailValidation } from '../../../../../utils/useEmailValidation';
import type {
  CoApplicantPersonFormGroupData,
  DenmarkFormData,
} from '../../FormData.dk.types';
import {
  getEmailRegisterOptions,
  phoneFieldOptions,
  socialSecurityNumberFieldOptions,
} from '../shared-config';

export function CoApplicantPersonFormGroup() {
  const { t } = useTranslation();

  const { baseUrl, axoFormData } = useAxoFormContext<DenmarkFormData>();

  const mainApplicantSSN = axoFormData['Person/SocialSecurityNumber'];

  const { setValue, watch } = useFormContext();

  const { EmailPrompt, formError, enableEmailValidation } = useEmailValidation({
    baseUrl,
    email: watch('CoCustomer/Email'),
    setEmail: (email) => setValue('CoCustomer/Email', email),
  });

  const emailRegisterOptions = getEmailRegisterOptions(enableEmailValidation);

  const ssnRegisterOptions: RegisterOptions = useMemo(
    () => ({
      ...socialSecurityNumberFieldOptions.registerOptions,
      validate: (value) => {
        if (value === mainApplicantSSN)
          return t('SocialSecurityNumber.error.unique');
      },
    }),
    [mainApplicantSSN, t]
  );

  return (
    <>
      <EmailFieldUI<CoApplicantPersonFormGroupData>
        name="CoCustomer/Email"
        label={t('Email.label', { context: 'coapp' })}
        data-testid="email-input"
        registerOptions={emailRegisterOptions}
        error={formError}
        confirmationPrompt={<EmailPrompt />}
      />

      <PhoneFieldUI<CoApplicantPersonFormGroupData>
        name="CoCustomer/MobilePhoneNumber"
        label={t('MobilePhoneNumber.label', { context: 'coapp' })}
        data-testid="phone-number-input"
        {...phoneFieldOptions}
      />

      <NumberFieldUI<CoApplicantPersonFormGroupData>
        name="CoPerson/SocialSecurityNumber"
        label={t('SocialSecurityNumber.label', { context: 'coapp' })}
        data-testid="ssn-input"
        placeholder={t('SocialSecurityNumber.placeholder')}
        format="******-****"
        {...socialSecurityNumberFieldOptions}
        registerOptions={ssnRegisterOptions}
      />
    </>
  );
}
